import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { AppExceptionModel } from '../../models/app-exception.model';
import { Article } from '../../models/article.model';
import * as articleActions from '../actions/article.actions';

export interface ArticleState extends EntityState<Article> {
  loading: boolean;
  loaded: boolean;
  inProgress: boolean;
  error: AppExceptionModel | undefined;
}

export const articleAdapter: EntityAdapter<Article> =
  createEntityAdapter<Article>();

export const initialState: ArticleState = articleAdapter.getInitialState({
  loading: false,
  loaded: false,
  inProgress: false,
  error: undefined,
});

export const articleReducer = createReducer(
  initialState,
  on(articleActions.getArticles, (state, {}) => ({
    ...state,
    loading: true,
    loaded: false,
    error: undefined,
  })),
  on(articleActions.getArticle, (state, {}) => ({
    ...state,
    error: undefined,
  })),
  on(articleActions.loadArticle, (state, { articles }) => {
    if (articles?.length > 0) {
      return articleAdapter.addMany(articles, {
        ...state,
        loading: false,
        loaded: true,
        inProgress: false,
        error: undefined,
      });
    } else {
      return {
        ...state,
        loading: false,
        loaded: true,
        inProgress: false,
        error: undefined,
      };
    }
  }),
  on(articleActions.getArticlesFailure, (state, { error }) => ({
    ...state,
    loading: false,
    loaded: true,
    error: error,
  })),
  on(articleActions.create, (state, { article }) => ({
    ...state,
    error: undefined,
    inProgress: true,
  })),
  on(articleActions.createSuccess, (state, { article }) =>
    articleAdapter.addOne(article, {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(articleActions.createFailure, (state, { error }) => ({
    ...state,
    error: error,
    inProgress: false,
  })),
  on(articleActions.update, (state, { article }) => ({
    ...state,
    error: undefined,
    inProgress: true,
  })),
  on(articleActions.updateSuccess, (state, { article }) =>
    articleAdapter.upsertOne(article, {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(articleActions.updateFailure, (state, { error }) => ({
    ...state,
    error: error,
    inProgress: false,
  })),
  on(articleActions.deleteAction, (state, { id }) => ({
    ...state,
    error: undefined,
    inProgress: true,
  })),
  on(articleActions.deleteSuccess, (state, { id }) =>
    articleAdapter.removeOne(id, {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(articleActions.deleteFailure, (state, { error }) => ({
    ...state,
    inProgress: false,
  })),
  on(articleActions.clear, (state) => initialState),
);
